<template>
  <v-card-text v-bind="$attrs" style="position: relative">
    <v-overlay absolute :value="placeLoading">
      <v-progress-circular size="50" color="primary" indeterminate />
    </v-overlay>
    <v-row>
      <v-col :cols="showSuite ? 8 : 12">
        <v-combobox
          v-model="tempSelectedAddress"
          :items="streetItems"
          no-filter
          label="Street Address"
          :search-input.sync="searchAddress"
          :loading="addressLoading"
          clearable
          :disabled="disabled"
          @change="fillAddress"
          @focus="getAddresses"
        >
          <template #item="{ item, attrs, on}">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.text }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.secondary_text }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </v-col>
      <v-col v-if="showSuite" cols="4">
        <v-text-field
          v-model="address.street2"
          label="Suite #"
          :disabled="disabled"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="5">
        <v-combobox
          v-model="address.city"
          :search-input.sync="searchCity"
          label="City"
          :items="cityItems"
          :loading="citiesLoading"
          required
          :disabled="disabled"
          autocomplete="dontshow"
        />
      </v-col>
      <v-col cols="4">
        <StateSelect v-model="address.state" :country="address.country" :disabled="disabled" />
      </v-col>
      <v-col cols="3">
        <v-text-field
          v-model="address.postal"
          label="Zip / Postal"
          required
          mask="#####"
          name="propertyZip"
          :disabled="disabled"
          autocomplete="dontshow"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <v-combobox
          v-model="address.county"
          label="County"
          :items="counties"
          :loading="countiesLoading"
          required
          item-text="name"
          item-value="name"
          :disabled="disabled"
          autocomplete="dontshow"
        />
      </v-col>
      <v-col cols="6">
        <v-autocomplete
          v-model="address.country"
          label="Country"
          :items="getCountries"
          required
          item-text="name"
          item-value="code"
          :disabled="disabled"
          autocomplete="dontshow"
        />
      </v-col>
    </v-row>
  </v-card-text>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { debounce } from 'lodash-es';

import { ServiceFactory } from '@/services/ServiceFactory';
import { IAddress, ICoordinates } from '@/utils/interfaces';
import { Nullable } from '@/utils/types';
import { NotificationMutations } from '@/store/types/mutation-types';
import { MIN_CHAR_COUNT } from '@/bundles/App/consts';
import { externalService } from '@/bundles/GroupFilters/factory/externalServiceFactory';

import StateSelect from '@/bundles/App/components/inputs/StateSelect.vue';

const GeoService = ServiceFactory.get('geo');

interface ITempSelectedAddress {
  text: string;
  value: string;
  place_id: string;
  secondary_text: string;
}

interface ICommonAddressWidget {
  placeLoading: boolean;
  tempSelectedAddress: Nullable<ITempSelectedAddress>;
  streetItems: Array<{[key: string]: string}>;
  searchAddress: Nullable<string>;
  addressLoading: boolean;
  coordinates: ICoordinates;
  isFirstLoad: boolean;
  counties: string[];
  countiesLoading: boolean;
  citiesLoading: boolean;
  cityItems: string[];
  searchCity: Nullable<string>;
}

const CUSTOM_ID = 'custom';

export default Vue.extend({
  name: 'CommonAddressWidget',

  components: { StateSelect },

  props: {
    name: {
      type: String,
      default: ''
    },
    value: {
      type: Object as PropType<IAddress>,
      default: () => ({
        street: '',
        street2: '',
        city: '',
        state: 'TX',
        postal: '',
        county: '',
        country: 'US'
      })
    },
    showSuite: Boolean,
    source: {
      type: String,
      default: 'property'
    },
    disabled: Boolean,
  },

  data: (): ICommonAddressWidget => ({
    placeLoading: false,
    tempSelectedAddress: null,
    streetItems: [],
    searchAddress: null,
    addressLoading: false,
    coordinates: {
      lng: null,
      lat: null
    },
    isFirstLoad: true,
    counties: [],
    countiesLoading: false,
    citiesLoading: false,
    cityItems: [],
    searchCity: null
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken,
      initialCountry: (state: any) => state.activeAccount.country
    }),
    ...mapGetters({
      getCountries: 'countries'
    }),
    address: {
      get (): IAddress {
        return this.value;
      },
      set (value: IAddress) {
        this.$emit('input', value);
      }
    }
  },

  watch: {
    'address.street': {
      handler (value: string) {
        if (value) {
          this.searchAddress = value;
          this.addCustomStreet(value);
          this.setSavedAddress();
          return;
        }

        this.searchAddress = value;
      },
      immediate: true
    },
    searchAddress (newValue: string | null, prevValue: string | null) {
      const selectedAddress = this.streetItems.find(street => street.text === newValue)?.value;

      if (!newValue || (newValue === prevValue) || selectedAddress === this.address.street) return;

      this.debouncedGetAddress(this, newValue);
    },
    'address.state': {
      handler (value: string) {
        if (!value) {
          this.counties = [];
          return;
        }

        this.getCounties(value);
      },
      immediate: true
    },
    'searchCity' (value) {
      if (value && value.length >= MIN_CHAR_COUNT) {
        this.getCitiesDebounced(this);
      }
    }
  },

  mounted () {
    this.address.country = this.address.country || this.initialCountry;
    this.getUniqueCities();
  },

  methods: {
    async getCounties (state) {
      try {
        this.countiesLoading = true;

        const params = [
          { name: 'state', value: state },
        ];

        const { data } = await GeoService.getCountiesByState({ token: this.token, params });

        this.counties = data.map(item => item.county_nam);
      } catch (error: any) {
        const notification = { ...error };
        this.addNotification(notification);
      } finally {
        this.countiesLoading = false;
      }
    },
    ...mapMutations('Notifications', {
      addNotification: NotificationMutations.ADD_NOTIFICATION
    }),
    debouncedGetAddress: debounce(function (vm, newValue) {
      vm.getAddressHandler(newValue);
    }, 500),
    async getAddresses () {
      if (this.isFirstLoad && this.address.street) {
        await this.getAddressHandler(this.address.street);
        this.isFirstLoad = false;
        this.setSavedAddress();
      }
    },
    async fillAddress (item: Nullable<ITempSelectedAddress | string>) {
      const isObject = typeof item === 'object' && item !== null;

      if (typeof item === 'string') {
        this.updateStreet(item);
        return;
      }

      if (!isObject) {
        this.address.street = null;
        return;
      }

      if (item?.place_id !== CUSTOM_ID) {
        return this.getPlace({
          id: (item as ITempSelectedAddress).value,
          street2: this.address.street2 || '',
          street: (item as ITempSelectedAddress).text
        });
      }

      this.updateStreet(item?.text);
    },
    updateStreet (value: string) {
      this.address.street = value;
      this.updateName(value);
    },
    updateName (streetName: Nullable<string>) {
      this.$emit('update:name', streetName);
    },
    setSavedAddress () {
      const selectedAddress = this.streetItems.find((item) => {
        const isStreetMatch = item.text === this.searchAddress;
        const isCityCountryMatch = item.secondary_text?.includes(this.address.city || '') &&
          item.secondary_text?.includes(this.address.country || '');

        if (isStreetMatch && isCityCountryMatch) {
          return item;
        }
      });

      if (selectedAddress) {
        this.tempSelectedAddress = selectedAddress as any;
        return;
      }

      this.tempSelectedAddress = this.streetItems.find(item => item.place_id === CUSTOM_ID) as any;
    },
    async getPlace ({ id, street2, street }: { id: string, street2: string, street: string }) {
      try {
        this.placeLoading = true;
        const { data } = await GeoService.getPlace({ token: this.token, id });

        data.address = Object.assign(data.address, { street2 });
        this.address = { ...data.address, street };

        this.coordinates = {
          lng: data.address.location.marker.lng,
          lat: data.address.location.marker.lat
        };
        this.$emit('updateCoordinates', { coordinates: this.coordinates, place_id: id });
        this.updateName(street);
      } catch (error: any) {
        const notification = { ...error };
        this.addNotification(notification);
      } finally {
        this.placeLoading = false;
      }
    },
    async getAddressHandler (value: string) {
      try {
        this.addressLoading = true;
        this.streetItems = [];
        const { data } = await GeoService.getAddressByKeyword({ key: value, token: this.token });

        this.streetItems = data.results.map(item => ({
          text: item.structured_formatting.main_text,
          value: item.place_id,
          place_id: item.place_id,
          secondary_text: item.structured_formatting.secondary_text
        }));

        this.addCustomStreet(value);
      } catch (error: any) {
        const notification = { ...error };
        this.addNotification(notification);
      } finally {
        this.addressLoading = false;
      }
    },
    getCitiesDebounced: debounce(function (vm: any) {
      vm.getUniqueCities();
    }, 400),
    async getUniqueCities () {
      try {
        this.citiesLoading = true;
        let payload = { source: this.source };

        if (this.searchCity) {
          payload = Object.assign(payload, { keyword: encodeURIComponent(this.searchCity as string) });
        }
        this.cityItems = await externalService.getCities(payload);
      } catch (error: any) {
        const notification = { ...error };
        this.addNotification(notification);
      } finally {
        this.citiesLoading = false;
      }
    },
    addCustomStreet (value: string) {
      const customStreet = { text: value, value, place_id: CUSTOM_ID, secondary_text: '' };

      this.streetItems.unshift(customStreet);
    }
  }
});
</script>
